import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { signInAnonymously, signOut, onAuthStateChanged } from 'firebase/auth';
import {
  collection, onSnapshot, orderBy, query, where, getDocs,
} from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions, storage } from '../lib/firebase.js';
import { registerForPush } from '../lib/messaging.js';
import TicketChat from '../components/TicketChat.jsx';
import SEO from '../components/SEO.jsx';

// QuickReport — the page behind a printed QR sticker (2026-08-29).
//
// Someone standing in front of a broken machine scans the code and lands here.
// No account, no password, no email: we sign them in anonymously, claim the
// access point (which grants a customer-role account bound to the vendor), and
// show a three-field form. After submitting they keep live status and can chat
// with the technician — because it IS a real account, just a nameless one
// living on their device.
//
// Route: /r/:vendorId/:accessPointId?t=<token>

export default function QuickReport() {
  const { vendorId, accessPointId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('t');

  const [phase, setPhase] = useState('loading'); // loading | ready | error
  const [error, setError] = useState(null);
  const [ap, setAp] = useState(null);            // { vendorName, label, siteName, address, defaultSkillCategoryId }
  const [skills, setSkills] = useState([]);
  const [uid, setUid] = useState(null);
  const [myTickets, setMyTickets] = useState([]);

  // ---- Bootstrap: anonymous session → claim access point → load skills ----
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!vendorId || !accessPointId || !token) {
        setError('This QR link is incomplete. Ask the service team for a new sticker.');
        setPhase('error');
        return;
      }
      try {
        // CRITICAL (review 2026-08-29): auth.currentUser is null until the SDK
        // finishes restoring the persisted session from IndexedDB, which on a
        // QR scan (always a cold tab) is *after* this effect runs. Without
        // awaiting readiness we'd mint a fresh anonymous account on every scan
        // — losing the returning visitor's history AND signing out any admin
        // who scans their own sticker. authStateReady() is the fix.
        if (auth.authStateReady) await auth.authStateReady();
        if (!auth.currentUser) {
          await signInAnonymously(auth);
        }
        const result = await httpsCallable(functions, 'claimAccessPoint')({
          vendorId, accessPointId, token,
        });
        if (cancelled) return;

        // Pick up the freshly granted custom claims before touching Firestore.
        if (result.data?.claimsGranted) {
          await auth.currentUser.getIdToken(true);
        }
        setAp(result.data);
        setUid(auth.currentUser?.uid || null);

        const snap = await getDocs(collection(db, 'vendors', vendorId, 'skillCategories'));
        if (cancelled) return;
        setSkills(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setPhase('ready');
      } catch (err) {
        if (cancelled) return;
        setError(prettyError(err));
        setPhase('error');
      }
    })();
    return () => { cancelled = true; };
  }, [vendorId, accessPointId, token]);

  // ---- Live view of the tickets this device has reported ----
  useEffect(() => {
    if (phase !== 'ready' || !uid || !vendorId) return;
    const q = query(
      collection(db, 'vendors', vendorId, 'tickets'),
      where('customerUid', '==', uid),
      orderBy('createdAt', 'desc'),
    );
    return onSnapshot(q, (snap) => {
      setMyTickets(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }, () => {});
  }, [phase, uid, vendorId]);

  // Keep uid fresh if the anonymous session is (re)established.
  useEffect(() => onAuthStateChanged(auth, (u) => setUid(u?.uid || null)), []);

  if (phase === 'loading') {
    return <Shell><div className="placeholder">Connecting…</div></Shell>;
  }

  if (phase === 'error') {
    return (
      <Shell>
        <div className="card" style={{ padding: 20 }}>
          <h2 style={{ marginTop: 0, fontSize: 18 }}>Can't open this code</h2>
          <p style={{ fontSize: 15, lineHeight: 1.5 }}>{error}</p>
          {/* The commonest cause: a staff member scanned their own sticker. */}
          {auth.currentUser && !auth.currentUser.isAnonymous && (
            <button
              className="btn-primary"
              style={{ width: '100%', marginTop: 8 }}
              onClick={() => signOut(auth).then(() => window.location.reload())}
            >
              Sign out and report as a visitor
            </button>
          )}
        </div>
      </Shell>
    );
  }

  return (
    <Shell vendorName={ap?.vendorName} label={ap?.label}>
      <ReportForm
        vendorId={vendorId}
        accessPointId={accessPointId}
        ap={ap}
        skills={skills}
      />
      {myTickets.length > 0 && (
        <section style={{ marginTop: 28 }}>
          <h2 style={{ fontSize: 17 }}>Your reports</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {myTickets.map((t) => (
              <WalkUpTicketRow key={t.id} t={t} vendorId={vendorId} uid={uid} />
            ))}
          </div>
        </section>
      )}
    </Shell>
  );
}

// ---------------------------------------------------------------------------

function Shell({ children, vendorName, label }) {
  return (
    <div className="requester" style={{ maxWidth: 620, margin: '0 auto' }}>
      <SEO title="Report an issue" noIndex />
      <header className="requester-header">
        <div className="brand-icon-row">
          <div>
            <div className="brand-vendor">{vendorName || 'Giraops'}</div>
            <small style={{ color: 'var(--muted)' }}>
              {label ? `Report an issue · ${label}` : 'Report an issue'}
            </small>
          </div>
        </div>
      </header>
      <main className="requester-main">{children}</main>
    </div>
  );
}

function ReportForm({ vendorId, accessPointId, ap, skills }) {
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState(ap?.address || '');
  const [skillId, setSkillId] = useState(ap?.defaultSkillCategoryId || '');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  function pickFile(e) {
    setFiles([...files, ...Array.from(e.target.files || [])].slice(0, 3));
    e.target.value = '';
  }

  async function submit(e) {
    e.preventDefault();
    if (!skillId || !description.trim() || !fullName.trim() || !phone.trim() || busy) return;
    setBusy(true); setError(null);
    try {
      const photoUrls = [];
      // Path carries the uploader's uid so storage rules can tie every walk-up
      // object to a banable identity (review finding: uploads were untraceable).
      const tempId = `walkup-${auth.currentUser?.uid || 'anon'}-${Date.now()}`;
      for (const file of files) {
        const path = `vendors/${vendorId}/tickets/${tempId}/photos/${file.name}`;
        const r = storageRef(storage, path);
        await uploadBytes(r, file);
        photoUrls.push(await getDownloadURL(r));
      }
      await httpsCallable(functions, 'submitTicket')({
        skillCategoryId: skillId,
        description: description.trim(),
        photoUrls,
        accessPointId,
        contact: {
          fullName: fullName.trim(),
          phone: phone.trim(),
          address: address.trim(),
        },
      });
      setDone(true);
      setDescription('');
      setFiles([]);
    } catch (err) {
      setError(prettyError(err));
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div className="card" style={{ padding: 20, background: '#f0fdf4', border: '1px solid #86efac' }}>
        <h2 style={{ marginTop: 0, fontSize: 18 }}>Reported — help is on the way</h2>
        <p style={{ fontSize: 15, lineHeight: 1.5, marginBottom: 12 }}>
          We're contacting the on-call technician now. Keep this page open (or scan the
          code again) to follow progress and message them directly.
        </p>
        {/* Ask for notifications HERE, not on arrival: a stranger who just
            scanned a sticker has no reason to say yes to a permission prompt,
            but someone who has just reported a fault very much wants to hear
            the reply. Without this the tech's chat message never reaches them. */}
        <PushOptIn />
        <button className="btn-ghost" onClick={() => setDone(false)}>Report another issue</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="card">
      <h2 style={{ marginTop: 0, fontSize: 18 }}>What's wrong?</h2>
      {ap?.siteName && (
        <div style={{
          background: '#fef3c7', border: '1px solid #fcd34d', borderLeft: '3px solid #f97316',
          borderRadius: 6, padding: '8px 12px', marginBottom: 14, fontSize: 14,
        }}>
          <strong>Location:</strong> {ap.siteName}
        </div>
      )}

      <label>Your name</label>
      <input value={fullName} onChange={(e) => setFullName(e.target.value)} required autoComplete="name" />

      <label style={{ marginTop: 12 }}>Best number to call you back</label>
      <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} required autoComplete="tel" placeholder="0412 345 678" />

      <label style={{ marginTop: 12 }}>Address / where you are</label>
      <input
        value={address}
        onChange={(e) => setAddress(e.target.value)}
        placeholder="Unit 4, 12 Smith St — or the building and level"
        autoComplete="street-address"
      />

      <label style={{ marginTop: 12 }}>What kind of problem?</label>
      <select value={skillId} onChange={(e) => setSkillId(e.target.value)} required>
        <option value="">Choose one…</option>
        {skills.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
      </select>

      <label style={{ marginTop: 12 }}>Describe what's happening</label>
      <textarea
        rows={4}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        maxLength={2000}
        required
        placeholder="e.g. No power to the kitchen since about 7pm, the switchboard is clicking."
        style={{
          width: '100%', padding: '10px 12px', border: '1px solid var(--border)',
          borderRadius: 'var(--radius)', fontFamily: 'inherit', fontSize: 15,
        }}
      />

      <label style={{ marginTop: 12 }}>Add a photo (optional, helps a lot)</label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
        {files.map((f, i) => (
          <div key={i} className="file-chip">
            {f.name}
            <button type="button" onClick={() => setFiles(files.filter((_, x) => x !== i))}>x</button>
          </div>
        ))}
      </div>
      {files.length < 3 && (
        <input type="file" accept="image/*" capture="environment" onChange={pickFile} />
      )}

      <button type="submit" disabled={busy} className="btn-primary" style={{ marginTop: 16, width: '100%' }}>
        {busy ? 'Sending…' : 'Report the problem'}
      </button>
      {error && <div className="error">{error}</div>}
      <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 10, marginBottom: 0 }}>
        No account needed. Your details are shared with the service team so they can reach you.
      </p>
    </form>
  );
}

// One-tap notification opt-in for walk-up visitors. Silent no-op on browsers
// without push (iOS Safari before add-to-home-screen), already-granted, or
// previously-denied — never nag.
function PushOptIn() {
  const supported = typeof Notification !== 'undefined';
  const [state, setState] = useState(
    supported && Notification.permission === 'granted' ? 'on' : 'idle',
  );

  if (!supported || state === 'hidden' || (supported && Notification.permission === 'denied')) {
    return null;
  }
  if (state === 'on') {
    return (
      <div style={{ fontSize: 13.5, color: '#166534', marginBottom: 12 }}>
        🔔 You'll get a notification when the technician replies.
      </div>
    );
  }

  async function enable() {
    setState('busy');
    const token = await registerForPush();
    setState(token ? 'on' : 'hidden');
  }

  return (
    <button
      className="btn-primary"
      style={{ width: '100%', marginBottom: 12 }}
      disabled={state === 'busy'}
      onClick={enable}
    >
      {state === 'busy' ? 'Turning on…' : '🔔 Notify me when the technician replies'}
    </button>
  );
}

function WalkUpTicketRow({ t, vendorId, uid }) {
  const [chatOpen, setChatOpen] = useState(false);
  const label = statusLabel(t);
  return (
    <div className="card" style={{ padding: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
        <strong>{t.skillName}</strong>
        <span className="badge" style={{ background: 'transparent', border: `1px solid ${label.color}`, color: label.color }}>
          {label.text}
        </span>
      </div>
      <div style={{ marginTop: 6, fontSize: 14 }}>
        {t.description?.length > 120 ? `${t.description.slice(0, 120)}…` : t.description}
      </div>
      {(t.assignedTo || t.chat) && (
        <button className="btn-primary" style={{ width: '100%', marginTop: 10 }} onClick={() => setChatOpen(true)}>
          💬 Message {t.assignedTo?.name?.split(' ')[0] || 'the team'}
        </button>
      )}
      {chatOpen && (
        <TicketChat
          vendorId={vendorId}
          ticket={t}
          me={{ uid, name: t.customerName || 'Me', role: 'customer' }}
          onClose={() => setChatOpen(false)}
        />
      )}
    </div>
  );
}

function statusLabel(t) {
  const s = t.status;
  if (s === 'NEW' || s === 'ROUTING') return { text: 'Finding a technician…', color: '#f59e0b' };
  if (s?.startsWith('RINGING')) return { text: 'Calling the team…', color: '#f59e0b' };
  if (s === 'ASSIGNED') return { text: `${t.assignedTo?.name || 'A technician'} is on it`, color: '#10b981' };
  if (s === 'CLOSED') return { text: 'Resolved', color: '#10b981' };
  if (s === 'ABANDONED') return { text: 'Please call the team', color: '#dc2626' };
  return { text: s || '', color: '#64748b' };
}

function prettyError(err) {
  const m = err?.message || '';
  if (m.includes('deactivated')) return 'This QR code has been switched off. Please contact the service team directly.';
  if (m.includes('not valid') || m.includes('no longer valid')) return 'This QR code isn\'t valid any more. Ask for an up-to-date sticker.';
  if (m.includes('administrator') || m.includes('responder')) return m;
  if (m.includes('different company')) return m;
  if (m.includes('several issues')) return m;
  if (m.includes('admin-restricted') || m.includes('CONFIGURATION_NOT_FOUND')) {
    return 'Guest access is not switched on for this company yet. Please contact the service team.';
  }
  if (m.includes('no longer has access')) return 'This device can no longer report issues here. Please call the service team.';
  // Never surface raw internal callable text on a public page.
  return 'Something went wrong. Please try again, or call the service team directly.';
}
